import { AppBar, Box, Chip, Toolbar, Typography } from "@mui/material";
import { ReactElement } from "react";
import { useLocation } from "react-router-dom";

import DOCK_TOP_BAND_HEIGHT from "~/components/dock/dockChrome";
import MobileFernieLauncher from "~/components/mobile/MobileFernieLauncher";
import PageBreadcrumbs from "~/components/PageBreadcrumbs";
import Config from "~/config";
import { useAppSelector } from "~/hooks/reduxHooks";
import useIsDockShellRoute from "~/hooks/useIsDockShellRoute";
import AccountMenu from "~/layout/AccountMenu";
import BrandMark from "~/layout/BrandMark";
import GlobalSearch from "~/layout/GlobalSearch";
import MobileSearchLauncher from "~/layout/mobile/MobileSearchLauncher";
import MobileTopBar from "~/layout/mobile/MobileTopBar";
import isMobileFirstPath from "~/layout/mobileFirstRoutes";
import PageButtons from "~/layout/PageButtons";
import TenantSwitcher from "~/layout/TenantSwitcher";

interface TopBarProps {
	children?: ReactElement;
}

const TopBar = ({ children }: TopBarProps) => {
	const pageLabel = useAppSelector((state) => state.layout.pageLabel);
	const pageIdentifier = useAppSelector((state) => state.layout.pageIdentifier);
	const showTopBar = useAppSelector((state) => state.layout.showTopBar);
	const isWideScreen = useAppSelector((state) => state.layout.isWideScreen);
	const location = useLocation();
	const isDock = useIsDockShellRoute();
	const isMobileFirst = isMobileFirstPath(location.pathname);
	const isHome = location.pathname === "/";

	if (!showTopBar) {
		return <>{children}</>;
	}

	if (isMobileFirst && !isWideScreen) {
		return (
			<>
				<MobileTopBar identifier={pageIdentifier} title={pageLabel ?? ""} />
				{children}
			</>
		);
	}

	return (
		<AppBar
			color="inherit"
			id="appHeader"
			position="static"
			sx={{
				backgroundColor: "transparent",
				backgroundImage: "none",
				boxShadow: 0,
				paddingTop: "env(safe-area-inset-top)",
				zIndex: 1000
			}}
		>
			<Toolbar
				sx={{
					gap: 1,
					pl: 1,
					...(isDock && {
						borderBottom: 1,
						borderColor: "divider",
						height: DOCK_TOP_BAND_HEIGHT,
						minHeight: DOCK_TOP_BAND_HEIGHT
					})
				}}
				variant="dense"
			>
				<Box sx={{ alignItems: "center", display: "flex", flex: 1, minWidth: 0, overflow: "hidden" }}>
					{isHome ? (
						<BrandMark />
					) : isWideScreen ? (
						<PageBreadcrumbs />
					) : (
						<Typography noWrap sx={{ margin: 0, padding: 0 }} variant="h6">
							{pageLabel} {pageIdentifier && ` / ${pageIdentifier}`}
						</Typography>
					)}
				</Box>
				{!isMobileFirst && isWideScreen && (
					<Box sx={{ flexShrink: 0 }}>
						<GlobalSearch />
					</Box>
				)}
				<Box sx={{ alignItems: "center", display: "flex", flex: 1, gap: 1, justifyContent: "flex-end" }}>
					{!isMobileFirst && !isWideScreen && (
						<Box sx={{ display: "flex" }}>
							<MobileSearchLauncher />
							<MobileFernieLauncher />
						</Box>
					)}
					<PageButtons />
					{!isMobileFirst && Config.environment !== "production" && (
						<Chip color="warning" label={Config.environment} size="small" variant="outlined" />
					)}
					<TenantSwitcher />
					<AccountMenu />
				</Box>
			</Toolbar>
			{children}
		</AppBar>
	);
};

export default TopBar;
